import { ChevronDownIcon } from '#/shared/ui/icons'
import { cn } from '#/shared/lib/utils'

export const MASS_REMOVED_REASON = {
  duplicate: 'duplicate',
  unsubscribed: 'unsubscribed',
  invalid: 'invalid',
  do_not_text: 'do_not_text',
  do_not_call: 'do_not_call',
  wrong_number: 'wrong_number',
  undeliverable: 'undeliverable',
  spam_blocked: 'spam_blocked',
  route_blocked: 'route_blocked',
  sender_paused: 'sender_paused',
  landline_or_voip: 'landline_or_voip',
  gate_rejected: 'gate_rejected',
  tmobile_daily_cap: 'tmobile_daily_cap',
} as const

export type MassRemovedReason =
  (typeof MASS_REMOVED_REASON)[keyof typeof MASS_REMOVED_REASON]

export interface MassRemovedRecipient {
  id: string
  name: string
  contact: string
  reason: MassRemovedReason
  detail?: string
  /** Selected rows collapsed into this contact (same person, multiple selected
   *  shipments). 1 for a single row; renders as `×N` beside the name when >1. */
  count: number
}

export interface MassRecipientsLabels {
  sending: string
  viewRemoved: string
  heading: string
  chip: (reason: MassRemovedReason, count: number) => string
  reason: (item: MassRemovedRecipient) => string
}

export const REMOVED_REASON_DOT_CLASS: Record<MassRemovedReason, string> = {
  [MASS_REMOVED_REASON.duplicate]: 'bg-gray-300',
  [MASS_REMOVED_REASON.unsubscribed]: 'bg-orange-400',
  [MASS_REMOVED_REASON.invalid]: 'bg-red-500',
  [MASS_REMOVED_REASON.do_not_text]: 'bg-orange-400',
  [MASS_REMOVED_REASON.do_not_call]: 'bg-orange-400',
  [MASS_REMOVED_REASON.wrong_number]: 'bg-red-500',
  [MASS_REMOVED_REASON.undeliverable]: 'bg-red-500',
  [MASS_REMOVED_REASON.spam_blocked]: 'bg-red-500',
  [MASS_REMOVED_REASON.route_blocked]: 'bg-red-500',
  [MASS_REMOVED_REASON.sender_paused]: 'bg-orange-400',
  [MASS_REMOVED_REASON.landline_or_voip]: 'bg-red-500',
  [MASS_REMOVED_REASON.gate_rejected]: 'bg-orange-400',
  [MASS_REMOVED_REASON.tmobile_daily_cap]: 'bg-orange-400',
}
const DOT_CLASS = REMOVED_REASON_DOT_CLASS

const REASON_ORDER: readonly MassRemovedReason[] = [
  MASS_REMOVED_REASON.duplicate,
  MASS_REMOVED_REASON.unsubscribed,
  MASS_REMOVED_REASON.do_not_text,
  MASS_REMOVED_REASON.do_not_call,
  MASS_REMOVED_REASON.wrong_number,
  MASS_REMOVED_REASON.invalid,
  MASS_REMOVED_REASON.undeliverable,
  MASS_REMOVED_REASON.landline_or_voip,
  MASS_REMOVED_REASON.spam_blocked,
  MASS_REMOVED_REASON.route_blocked,
  MASS_REMOVED_REASON.sender_paused,
  MASS_REMOVED_REASON.gate_rejected,
  MASS_REMOVED_REASON.tmobile_daily_cap,
]

function Dot({ className }: { className: string }) {
  return <span className={cn('size-1.75 shrink-0 rounded-full', className)} />
}

function Chips({
  removed,
  labels,
}: {
  removed: readonly MassRemovedRecipient[]
  labels: MassRecipientsLabels
}) {
  // Sum `count` (not row length) so chips report row-level totals — the server
  // collapses multiple selected shipments of one contact into a single entry.
  const groups = REASON_ORDER.map((reason) => ({
    reason,
    count: removed
      .filter((item) => item.reason === reason)
      .reduce((sum, item) => sum + item.count, 0),
  })).filter((group) => group.count > 0)
  return (
    <div className="flex flex-wrap items-center gap-x-4.5 gap-y-1">
      {groups.map((group) => (
        <div key={group.reason} className="flex items-center gap-1.75">
          <Dot className={DOT_CLASS[group.reason]} />
          <p className="text-xs text-gray-500">
            {labels.chip(group.reason, group.count)}
          </p>
        </div>
      ))}
    </div>
  )
}

function RemovedList({
  removed,
  labels,
}: {
  removed: readonly MassRemovedRecipient[]
  labels: MassRecipientsLabels
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-500">{labels.heading}</p>
      <div className="h-px w-full bg-gray-200" />
      <div className="flex flex-col">
        {removed.map((item, index) => (
          <div
            key={item.id}
            className={cn(
              'flex items-center justify-between gap-2.5 py-2',
              index < removed.length - 1 && 'border-b border-gray-200',
            )}
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <Dot className={DOT_CLASS[item.reason]} />
              <p className="truncate text-sm text-gray-900">{item.name}</p>
              {item.count > 1 && (
                <span className="shrink-0 rounded bg-gray-200 px-1.25 py-0.25 text-xs font-medium text-gray-500 tabular-nums">
                  ×{item.count}
                </span>
              )}
              <p className="truncate text-sm text-gray-500">{item.contact}</p>
            </div>
            <p className="shrink-0 text-sm text-gray-500">
              {labels.reason(item)}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

interface MassRecipientsProps {
  totalSelected: number
  willReceive: number
  removed: MassRemovedRecipient[]
  expanded: boolean
  onToggle: () => void
  labels: MassRecipientsLabels
  className?: string
}

export function MassRecipients({
  removed,
  expanded,
  onToggle,
  labels,
  className,
}: MassRecipientsProps) {
  const hasRemoved = removed.length > 0
  return (
    <div
      className={cn(
        'flex flex-col gap-2.5 border-b border-gray-200 bg-gray-50 px-5 py-3.5',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2.5">
        <p className="text-sm font-medium text-gray-900">{labels.sending}</p>
        {hasRemoved && (
          <button
            type="button"
            onClick={onToggle}
            className="flex shrink-0 cursor-pointer items-center gap-1.25 text-sm font-medium text-primary select-none"
          >
            {labels.viewRemoved}
            <ChevronDownIcon
              className={cn(
                'size-4 transition-transform duration-200',
                expanded && 'rotate-180',
              )}
            />
          </button>
        )}
      </div>
      {hasRemoved &&
        (expanded ? (
          <RemovedList removed={removed} labels={labels} />
        ) : (
          <Chips removed={removed} labels={labels} />
        ))}
    </div>
  )
}
